/**
 * Deep-clone widgets / reports with fresh ids so copies never collide.
 */

function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function cloneJson(value) {
  if (value == null) return value;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      /* fall through */
    }
  }
  return JSON.parse(JSON.stringify(value));
}

export function cloneWidget(widget, { id = newId() } = {}) {
  const next = cloneJson(widget) || {};
  next.id = id;
  return next;
}

function remapQueryId(widget, queryIdMap) {
  if (!widget?.reportQueryId) return widget;
  const mapped = queryIdMap[widget.reportQueryId];
  if (!mapped) return widget;
  return { ...widget, reportQueryId: mapped };
}

export function cloneTab(tab, { queryIdMap = {} } = {}) {
  const widgetIdMap = {};
  const widgets = (tab.widgets || []).map((widget) => {
    const id = newId();
    widgetIdMap[widget.id] = id;
    return remapQueryId(cloneWidget(widget, { id }), queryIdMap);
  });
  const layout = (tab.layout || []).map((item) => ({
    ...cloneJson(item),
    i: widgetIdMap[item.i] || newId(),
  }));
  return {
    ...cloneJson(tab),
    id: newId(),
    widgets,
    layout,
  };
}

export function cloneReport(report, { nameSuffix = ' (کپی)' } = {}) {
  const queryIdMap = {};
  const queries = (report.queries || []).map((query) => {
    const id = newId();
    if (query?.id) queryIdMap[query.id] = id;
    return { ...cloneJson(query), id };
  });
  const tabs = (report.tabs || []).map((tab) => cloneTab(tab, { queryIdMap }));
  return {
    ...cloneJson(report),
    id: newId(),
    name: `${report.name || 'گزارش'}${nameSuffix}`,
    is_main_dashboard: false,
    queries,
    tabs,
  };
}

export function nextLayoutItem(layout = [], source, { w, h } = {}) {
  const width = Math.max(2, Number(w ?? source?.w) || 2);
  const height = Math.max(2, Number(h ?? source?.h) || 2);
  if (!layout.length) {
    return { i: source?.i, x: 0, y: 0, w: width, h: height, minW: 2, minH: 2, maxW: 12 };
  }
  const lastY = Math.max(...layout.map((l) => l.y || 0));
  const rowItems = layout.filter((l) => (l.y || 0) === lastY);
  const usedRight = rowItems.reduce((max, l) => Math.max(max, (l.x || 0) + (l.w || 0)), 0);
  if (usedRight + width <= 12) {
    return { i: source?.i, x: usedRight, y: lastY, w: width, h: height, minW: 2, minH: 2, maxW: 12 };
  }
  const nextY = Math.max(...layout.map((l) => (l.y || 0) + (l.h || 1)));
  return { i: source?.i, x: 0, y: nextY, w: width, h: height, minW: 2, minH: 2, maxW: 12 };
}

export { newId as genCloneId };
